import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { venueApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import SEO from '../components/SEO';
import {
  MapPin, Users, DollarSign, Loader2, Building2, ArrowLeft,
  Star, Percent, CheckCircle, Clock, Phone, Mail, Calendar as CalendarIcon,
  ChevronLeft, ChevronRight, Share2, Heart, ExternalLink
} from 'lucide-react';

// Amenity icons mapping
const AMENITY_ICONS = {
  'WiFi': '📶',
  'Parking': '🅿️',
  'Kitchen': '🍳',
  'Sound System': '🔊',
  'Lighting': '💡',
  'Stage': '🎭',
  'Green Room': '🚪',
  'Air Conditioning': '❄️',
  'Wheelchair Accessible': '♿',
  'Outdoor Space': '🌳',
  'Catering Available': '🍽️',
  'Bar Service': '🍸',
  'Security': '🔒',
  'Projector/Screen': '📽️',
  'Dance Floor': '💃',
};

const VenueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    event_date: '',
    event_type: '',
    guest_count: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadVenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadVenue = async () => {
    setLoading(true);
    try {
      const response = await venueApi.get(id);
      setVenue(response.data);
      
      // Track recently viewed venue
      const venueData = response.data;
      if (venueData && venueData.venue_id) {
        const viewedItem = {
          id: venueData.venue_id,
          name: venueData.name,
          image: venueData.photos && venueData.photos[0],
          category: venueData.venue_type || 'Venue',
          type: 'venue',
          viewedAt: new Date().toISOString()
        };
        
        try {
          const stored = localStorage.getItem('bookd_recently_viewed');
          let recentList = stored ? JSON.parse(stored) : [];
          // Remove if already exists
          recentList = recentList.filter(item => item.id !== venueData.venue_id);
          // Add to front
          recentList.unshift(viewedItem);
          // Keep only last 12
          recentList = recentList.slice(0, 12);
          localStorage.setItem('bookd_recently_viewed', JSON.stringify(recentList));
        } catch (e) {
          console.error('Failed to save recently viewed:', e);
        }
      }
    } catch (error) {
      console.error('Failed to load venue:', error);
      toast.error('Failed to load venue details');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/venues/${id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: venue.name,
          text: `Check out ${venue.name} on Bookd`,
          url: url
        });
      } catch (err) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // In production, this would send an email/notification to the venue owner
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Inquiry sent! The venue owner will contact you soon.');
      setShowInquiryDialog(false);
      setInquiryForm({
        name: '',
        email: '',
        phone: '',
        event_date: '',
        event_type: '',
        guest_count: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const nextImage = () => {
    if (venue?.photos?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % venue.photos.length);
    }
  };

  const prevImage = () => {
    if (venue?.photos?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + venue.photos.length) % venue.photos.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex justify-center items-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="min-h-screen pt-24 px-6">
        <div className="max-w-4xl mx-auto text-center py-20">
          <Building2 className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold mb-2">Venue Not Found</h1>
          <p className="text-muted-foreground mb-6">This venue may have been removed or is no longer available.</p>
          <Button onClick={() => navigate('/venues')} className="rounded-xl">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Venues
          </Button>
        </div>
      </div>
    );
  }

  const discountedHourly = venue.offers_discount && venue.discount_percent
    ? venue.hourly_rate * (1 - venue.discount_percent / 100)
    : null;

  return (
    <div className="min-h-screen pb-12">
      {/* SEO */}
      <SEO
        title={`${venue.name} - Venue on Bookd`}
        description={venue.description || `Book ${venue.name} for your next event. ${venue.city ? `Located in ${venue.city}.` : ''} Capacity: ${venue.capacity || 'varies'}.`}
        image={venue.photos?.[0]}
        url={`/venues/${id}`}
        type="place"
      />

      {/* Back Button */}
      <div className="pt-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/venues')}
            className="mb-4"
            data-testid="back-to-venues-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Venues
          </Button>
        </div>
      </div>

      {/* Image Gallery */}
      <div className="px-6">
        <div className="max-w-6xl mx-auto">
          <div className="relative aspect-[21/9] rounded-2xl overflow-hidden bg-muted">
            {venue.photos && venue.photos.length > 0 ? (
              <>
                <img
                  src={venue.photos[currentImageIndex]}
                  alt={`${venue.name} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
                {venue.photos.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {venue.photos.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Building2 className="h-24 w-24 text-muted-foreground" />
              </div>
            )}

            {/* Badges overlay */}
            <div className="absolute top-4 left-4 flex gap-2">
              {venue.offers_discount && (
                <Badge className="bg-green-500 text-white">
                  <Percent className="h-3 w-3 mr-1" />
                  {venue.discount_percent}% Off
                </Badge>
              )}
              {venue.is_available && (
                <Badge className="bg-primary">Available</Badge>
              )}
            </div>

            {/* Action buttons */}
            <div className="absolute top-4 right-4 flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="rounded-full"
                onClick={handleShare}
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Thumbnail strip */}
          {venue.photos && venue.photos.length > 1 && (
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
              {venue.photos.map((photo, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                    idx === currentImageIndex ? 'border-primary' : 'border-transparent'
                  }`}
                >
                  <img src={photo} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 mt-8">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                    {venue.name}
                  </h1>
                  <p className="text-muted-foreground flex items-center gap-2 mt-2">
                    <MapPin className="h-4 w-4" />
                    {[venue.address, venue.city, venue.state, venue.zip_code].filter(Boolean).join(', ')}
                  </p>
                </div>
                <Badge variant="outline" className="shrink-0 text-base px-3 py-1">
                  {venue.venue_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-6 mt-4">
                {venue.capacity && (
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">{venue.capacity}</span>
                    <span className="text-muted-foreground">guests</span>
                  </div>
                )}
                {venue.rating > 0 && (
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
                    <span className="font-medium">{venue.rating.toFixed(1)}</span>
                    <span className="text-muted-foreground">({venue.review_count} reviews)</span>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Description */}
            {venue.description && (
              <div>
                <h2 className="text-xl font-semibold mb-3">About This Venue</h2>
                <p className="text-muted-foreground whitespace-pre-line">{venue.description}</p>
              </div>
            )}

            <Separator />

            {/* Amenities */}
            {venue.amenities && venue.amenities.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold mb-4">Amenities</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {venue.amenities.map((amenity) => (
                    <div
                      key={amenity}
                      className="flex items-center gap-3 p-3 rounded-xl bg-muted/50"
                    >
                      <span className="text-xl">{AMENITY_ICONS[amenity] || '✓'}</span>
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Availability Calendar */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Check Availability</h2>
              <Card className="border-border/50">
                <CardContent className="pt-6">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < new Date()}
                    className="rounded-md mx-auto"
                  />
                  {selectedDate && (
                    <div className="mt-4 p-4 rounded-xl bg-primary/10 text-center">
                      <p className="font-medium">
                        Selected: {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                      </p>
                      <Button
                        className="mt-2 rounded-xl"
                        onClick={() => setShowInquiryDialog(true)}
                      >
                        Inquire for This Date
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <Card className="border-border/50 sticky top-24">
              <CardHeader>
                <CardTitle>Pricing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Hourly Rate */}
                {venue.hourly_rate && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Hourly Rate</span>
                    <div className="text-right">
                      {discountedHourly ? (
                        <>
                          <span className="text-lg font-bold text-primary">
                            ${discountedHourly.toFixed(0)}
                          </span>
                          <span className="text-sm text-muted-foreground line-through ml-2">
                            ${venue.hourly_rate}
                          </span>
                        </>
                      ) : (
                        <span className="text-lg font-bold">${venue.hourly_rate}</span>
                      )}
                      <span className="text-muted-foreground">/hr</span>
                    </div>
                  </div>
                )}

                {/* Daily Rate */}
                {venue.daily_rate && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Daily Rate</span>
                    <div>
                      <span className="text-lg font-bold">${venue.daily_rate}</span>
                      <span className="text-muted-foreground">/day</span>
                    </div>
                  </div>
                )}

                {/* Minimum Hours */}
                {venue.minimum_hours && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Minimum Booking</span>
                    <span>{venue.minimum_hours} hours</span>
                  </div>
                )}

                <Separator />

                {/* Discount Banner */}
                {venue.offers_discount && (
                  <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                      <Percent className="h-4 w-4" />
                      <span className="font-medium">{venue.discount_percent}% Off</span>
                    </div>
                    {venue.discount_description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {venue.discount_description}
                      </p>
                    )}
                  </div>
                )}

                {/* CTA Button */}
                <Dialog open={showInquiryDialog} onOpenChange={setShowInquiryDialog}>
                  <DialogTrigger asChild>
                    <Button className="w-full rounded-xl btn-glow" size="lg" data-testid="inquire-btn">
                      <Mail className="h-4 w-4 mr-2" />
                      Send Inquiry
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Inquire About {venue.name}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleInquirySubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="inquiry-name">Name *</Label>
                          <Input
                            id="inquiry-name"
                            value={inquiryForm.name}
                            onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                            required
                            className="rounded-xl"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="inquiry-email">Email *</Label>
                          <Input
                            id="inquiry-email"
                            type="email"
                            value={inquiryForm.email}
                            onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                            required
                            className="rounded-xl"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="inquiry-date">Event Date</Label>
                          <Input
                            id="inquiry-date"
                            type="date"
                            value={inquiryForm.event_date}
                            onChange={(e) => setInquiryForm({ ...inquiryForm, event_date: e.target.value })}
                            className="rounded-xl"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="inquiry-guests">Guest Count</Label>
                          <Input
                            id="inquiry-guests"
                            type="number"
                            placeholder="e.g., 50"
                            value={inquiryForm.guest_count}
                            onChange={(e) => setInquiryForm({ ...inquiryForm, guest_count: e.target.value })}
                            className="rounded-xl"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="inquiry-type">Event Type</Label>
                        <Input
                          id="inquiry-type"
                          placeholder="e.g., Wedding, Corporate Event, Photoshoot"
                          value={inquiryForm.event_type}
                          onChange={(e) => setInquiryForm({ ...inquiryForm, event_type: e.target.value })}
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="inquiry-message">Message</Label>
                        <Textarea
                          id="inquiry-message"
                          placeholder="Tell us about your event..."
                          value={inquiryForm.message}
                          onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                          className="rounded-xl min-h-[100px]"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full rounded-xl"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Send Inquiry'
                        )}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Quick Info */}
                <div className="pt-4 space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Instant response within 24 hours</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Free cancellation up to 48 hours</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenueDetail;
